import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DevHubAlert,
  DevHubAvatar,
  DevHubBox,
  DevHubButton,
  DevHubChip,
  DevHubCircularProgress,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubListItemText,
  DevHubPaper,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { Progress } from '@backstage/core-components';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { useTheme } from '@material-ui/core';
import { SystemContext } from '../../contexts/SystemContext';
import { useApplicationDisplay } from '../../contexts/ApplicationDisplayContext';
import AppInfoItem from '../common/AppInfoItem';
import AppTags from './AppTags';
import EditableApp360Field from './EditableApp360Field';
import { EditableBusinessCapabilities } from './EditableBusinessCapabilities';
import { EditableServiceOfferings } from './EditableServiceOfferings';
import { EditableServiceBusinessItems } from './EditableServiceBusinessItems';
import { FunctionalArea } from './types/InsightsTypes';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';
import AppEntityIcon from '../common/AppEntityIcon';
import slackIcon from '../../app/icons/slack_icon.svg';
import NovaIcon from '../agent/NovaIcon';

const serviceLevelDomains = [
  {
    label: '1 - Mission Critical',
    value: 1,
  },
  {
    label: '2 - Enterprise',
    value: 2,
  },
  {
    label: '3 - Departmental/Prod Support',
    value: 3,
  },
  {
    label: '4 - Dev/Lab',
    value: 4,
  },
];

const statuses = [
  { label: 'Active', value: 'Active' },
  {
    label: 'Pre-Prod',
    value: 'Pre-Prod',
  },
  { label: 'Retired', value: 'Retired' },
];

/**
 * The {@link InsightsAbout} component for the appInsightsPage
 */
export const InsightsAbout = () => {
  const { app, loading, error, getPolicyAccess } = useContext(SystemContext);
  const { config } = useApplicationDisplay();
  const theme = useTheme();
  const navigate = useNavigate();
  const palette = theme.palette as any;
  const [edit, setEdit] = useState(false);
  const [legalHold, setLegalHold] = useState(false);

  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const isNovaEnabled = ConfigHelper.instance.isFeatureEnabled(Feature.NOVA);
  const [slackChannels, setSlackChannels] = useState<string[]>([]);
  const [slackChannelNames, setSlackChannelNames] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (!app?.applicationId || !isNovaEnabled) return;
    const fetchSlackChannels = async () => {
      try {
        const { token } = await identityApi.getCredentials();
        const backendUrl = configApi.getString('backend.baseUrl');
        const headers: Record<string, string> = token
          ? { Authorization: `Bearer ${token}` }
          : {};
        const res = await fetch(
          `${backendUrl}/api/agent/app/${app.applicationId}/slack-settings`,
          { headers },
        );
        if (!res.ok) return;
        const data = await res.json();
        setSlackChannels(data.channels ?? []);
        setSlackChannelNames(data.channelNames ?? {});
      } catch {
        // silently fail — Slack channels are supplemental info
      }
    };
    fetchSlackChannels();
  }, [app?.applicationId, configApi, identityApi, isNovaEnabled]);

  const handleEdit = () => {
    setEdit(prevEdit => !prevEdit);
  };

  const isAppRetired = (): boolean => {
    return app?.status === 'Retired';
  };

  const canEditInfo = (): boolean => {
    return (
      config.canEdit &&
      getPolicyAccess('UpdateApplicationPolicy') &&
      !isAppRetired()
    );
  };

  const canEditCppTypes = (): boolean => {
    return (
      config.canEdit &&
      getPolicyAccess('ManageCppTypesPolicy') &&
      !isAppRetired()
    );
  };

  const canEditAppTags = (): boolean => {
    return (
      config.canManageTags &&
      (getPolicyAccess('ManageApplicationTagsPolicy') ||
        getPolicyAccess('UpdateTagPolicy')) &&
      !isAppRetired()
    );
  };

  const canEditFunctionalArea = (): boolean => {
    return (
      config.canEdit &&
      getPolicyAccess('FunctionalAreaEditConnectionPolicy') &&
      !isAppRetired()
    );
  };

  useEffect(() => {
    setLegalHold(
      app?.privacy?.dataMinimizationStatus?.legalHold?.isActive ?? false,
    );
  }, [app]);

  const sdlTeamName =
    app?.sdlTeamsPaged?.teams?.[0]?.sdlTeamName ?? 'Unassigned';

  const getFunctionalArea = (): FunctionalArea => {
    if (app?.functionalAreaConnection?.length > 0) {
      return app?.functionalAreaConnection[0];
    }
    return { name: 'Unassigned' } as FunctionalArea;
  };

  if (loading) {
    return <Progress />;
  } else if (error) {
    return (
      <DevHubAlert severity="error">Error loading application data</DevHubAlert>
    );
  }

  // Standard layout for all apps (including experimental)
  return (
    <>
      <DevHubBox padding="medium">
        {legalHold && (
          <DevHubBox mb={2}>
            <DevHubAlert severity="warning">
              This Application is on Legal Hold - Please contact
              eDiscovery@comcast.com if you have any questions regarding data
              that needs to be preserved
            </DevHubAlert>
          </DevHubBox>
        )}
        {isAppRetired() && (
          <DevHubBox mb={2} mt={-2}>
            <DevHubAlert severity="info">
              This application is retired and cannot be edited.
            </DevHubAlert>
          </DevHubBox>
        )}
        {app?.systemType === 'AiSystem' && (
          <DevHubBox mb={2}>
            <DevHubAlert severity="info">
              IOP configuration, SDLC pipeline, and D&A classification tabs are
              not shown for AI Systems.
            </DevHubAlert>
          </DevHubBox>
        )}
        <DevHubGrid container style={{ alignItems: 'stretch' }}>
          <DevHubGrid item xs={6}>
            <DevHubTypography variant="h2" fontColor="red">
              <EditableApp360Field
                applicationId={app?.applicationId}
                editField="appName"
                editComponent="textField"
                editValue={app?.name}
                edit={edit}
                hasEditPermission={canEditInfo()}
              >
                <AppEntityIcon name={app?.name} entity={app?.corporateEntity} />
              </EditableApp360Field>
            </DevHubTypography>
            {app?.systemType === 'AiSystem' && (
              <DevHubBox mt={1}>
                <DevHubChip label="AI System" color="primary" size="small" />
              </DevHubBox>
            )}
          </DevHubGrid>
          <DevHubGrid item xs={6}>
            <DevHubBox display="flex" justifyContent="right">
              {ConfigHelper?.instance?.isFeatureEnabled(Feature.USER_ROLES) && (
                <DevHubButton
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleEdit}
                  disabled={
                    !canEditInfo() &&
                    !canEditAppTags() &&
                    !canEditCppTypes() &&
                    !canEditFunctionalArea()
                  }
                  startIcon={
                    loading ? (
                      <DevHubCircularProgress thickness={8} size={16} />
                    ) : (
                      <DevHubDynamicIcon iconName="Edit" />
                    )
                  }
                >
                  {edit ? 'Stop Editing' : 'Edit'}
                </DevHubButton>
              )}
            </DevHubBox>
          </DevHubGrid>

          {/* ── Left column ── */}
          <DevHubGrid
            item
            xs={12}
            lg={7}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <EditableApp360Field
              applicationId={app?.applicationId}
              editField="description"
              editComponent="textArea"
              edit={edit}
              hasEditPermission={canEditInfo()}
              editValue={app?.description}
            >
              <DevHubTypography variant="subtitle1">
                <p>{app?.description}</p>
              </DevHubTypography>
            </EditableApp360Field>
            {!config.isExperimental && (
              <EditableApp360Field
                applicationId={app?.applicationId}
                editField="serviceDomain"
                editComponent="domainSelect"
                editValue={app?.serviceDomain}
                edit={edit}
                hasEditPermission={canEditInfo()}
              >
                <DevHubBox display="flex" alignItems="center">
                  This Application belongs to the Domain:
                  <DevHubBox ml={1} display="flex" alignItems="center">
                    <DevHubTypography weight="bold">
                      {app?.serviceDomain ? app?.serviceDomain : 'Unassigned'}
                    </DevHubTypography>
                  </DevHubBox>
                </DevHubBox>
              </EditableApp360Field>
            )}
          </DevHubGrid>

          {/* ── Right column ── */}
          <DevHubGrid
            item
            xs={12}
            lg={5}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <DevHubPaper slim style={{ padding: 16, boxSizing: 'border-box' }}>
              <DevHubTypography paragraph variant="h6">
                Application Owner
              </DevHubTypography>
              <EditableApp360Field
                applicationId={app?.applicationId}
                corporateEntity={app?.corporateEntity}
                editField="ownerLogin"
                editComponent="userSelect"
                edit={edit}
                hasEditPermission={canEditInfo()}
              >
                <DevHubGrid
                  container
                  padding="xsmall"
                  alignItems="center"
                  justifyContent="flex-start"
                >
                  <DevHubGrid item>
                    <DevHubAvatar>
                      <DevHubTypography fontColor="white">
                        {app?.owner?.firstName === null
                          ? 'N/A'
                          : app?.owner?.firstName?.charAt(0) +
                            app?.owner?.lastName?.charAt(0)}
                      </DevHubTypography>
                    </DevHubAvatar>
                  </DevHubGrid>
                  <DevHubGrid item>
                    <DevHubListItemText
                      primary={
                        app?.owner?.firstName === null
                          ? 'No Owner Assigned'
                          : `${app?.owner?.firstName} ${app?.owner?.lastName}`
                      }
                      secondary={<div>{app?.owner?.email}</div>}
                    />
                  </DevHubGrid>
                  {!app?.owner?.accountEnabled && (
                    <DevHubTypography weight="bold" color="secondary">
                      The current application owner is set to be disabled.
                      Please assign a new owner.
                    </DevHubTypography>
                  )}
                </DevHubGrid>
              </EditableApp360Field>
            </DevHubPaper>
          </DevHubGrid>

          {/* ── Nova info — 9 cols ── */}
          {isNovaEnabled && (
            <DevHubGrid item xs={12} lg={7} style={{ display: 'flex' }}>
              <DevHubBox
                p={2}
                style={{
                  flex: 1,
                  background:
                    'linear-gradient(135deg, rgba(239,21,65,0.08) 0%, rgba(227,6,227,0.08) 100%)',
                  borderRadius: 8,
                  borderLeft: '3px solid',
                  borderLeftColor: palette.devhub?.pink ?? '#E306E3',
                }}
              >
                <DevHubBox display="flex" alignItems="center" mb={1}>
                  <NovaIcon />
                  <DevHubTypography variant="subtitle1" weight="bold">
                    Meet Nova — Your AI App Assistant
                  </DevHubTypography>
                </DevHubBox>
                <DevHubTypography variant="body2" style={{ marginBottom: 8 }}>
                  The <strong>Nova</strong> tab lets you supercharge this
                  application with AI-powered support directly in Slack.
                </DevHubTypography>
                <DevHubTypography variant="body2" style={{ marginBottom: 8 }}>
                  Here's what you can do:
                </DevHubTypography>
                <DevHubBox
                  component="ul"
                  style={{ margin: '4px 0 8px 16px', padding: 0 }}
                >
                  {[
                    'Link Slack channels to this app so Nova can provide context-aware support',
                    "Write custom instructions that guide Nova's behavior in your channels",
                    'Add context sources; URLs, GitHub files, docs, or FLOW Context pools so Nova answers accurately',
                  ].map(item => (
                    <DevHubBox
                      component="li"
                      key={item}
                      style={{ marginBottom: 4 }}
                    >
                      <DevHubTypography variant="body2">
                        {item}
                      </DevHubTypography>
                    </DevHubBox>
                  ))}
                  <DevHubBox component="li" style={{ marginBottom: 4 }}>
                    <DevHubTypography variant="body2">
                      <strong>@nova-bot</strong> handles support questions,
                      on-call triage, and how-to queries using your app's
                      context
                    </DevHubTypography>
                  </DevHubBox>
                </DevHubBox>
                <DevHubTypography variant="body2" style={{ opacity: 0.7 }}>
                  Navigate to the <strong>Nova</strong> tab above to get
                  started.
                </DevHubTypography>
              </DevHubBox>
            </DevHubGrid>
          )}

          {/* ── Slack Channels — 3 cols ── */}
          {isNovaEnabled && (
            <DevHubGrid item xs={12} lg={5} style={{ display: 'flex' }}>
              <DevHubPaper
                slim
                style={{
                  padding: 16,
                  boxSizing: 'border-box',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <DevHubBox
                  display="flex"
                  alignItems="center"
                  mb={1}
                  style={{ gap: 8 }}
                >
                  <img src={slackIcon} alt="Slack" width={20} height={20} />
                  <DevHubTypography variant="h6" weight="bold">
                    Slack Channels
                  </DevHubTypography>
                  <DevHubTooltip title="Link Slack channels to this app from the Nova tab. Once linked, @nova-bot can answer support questions with your app's context directly in the channel.">
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      <DevHubDynamicIcon
                        iconName="HelpOutline"
                        fontSize="small"
                      />
                    </span>
                  </DevHubTooltip>
                </DevHubBox>
                <DevHubBox
                  display="flex"
                  flexWrap="wrap"
                  style={{ gap: 8, flex: 1 }}
                >
                  {slackChannels.length > 0 ? (
                    slackChannels.map(channelId => (
                      <DevHubChip
                        key={channelId}
                        label={`#${slackChannelNames[channelId] ?? channelId}`}
                        size="small"
                        style={{
                          background: palette.devhub.purple,
                          border: 'none',
                          color: 'white',
                        }}
                      />
                    ))
                  ) : (
                    <DevHubTypography variant="caption" color="textSecondary">
                      No channels linked yet.
                    </DevHubTypography>
                  )}
                </DevHubBox>
                <DevHubBox mt={2}>
                  <DevHubButton
                    variant="outlined"
                    size="small"
                    startIcon={<DevHubDynamicIcon iconName="OpenInNew" />}
                    onClick={() => navigate('nova')}
                    fullWidth
                  >
                    View &amp; Manage Channels
                  </DevHubButton>
                </DevHubBox>
              </DevHubPaper>
            </DevHubGrid>
          )}

          {/* ── App tags — full width, hidden when empty ── */}
          {app?.tags?.length > 0 && (
            <DevHubGrid item xs={12}>
              <AppTags
                edit={edit}
                tags={app?.tags}
                applicationId={app?.applicationId}
              />
            </DevHubGrid>
          )}
        </DevHubGrid>
      </DevHubBox>
      <DevHubGrid container tbMargin="medium">
        <DevHubGrid item xs={12} md={6}>
          <DevHubPaper fullHeight>
            <>
              <EditableApp360Field
                applicationId={app?.applicationId}
                editField="status"
                editComponent="statusSelect"
                options={statuses}
                editValue={app?.status}
                edit={edit}
                hasEditPermission={canEditInfo()}
              >
                <AppInfoItem
                  primaryText="Status:"
                  secondaryText={app?.status}
                />
              </EditableApp360Field>
              <EditableApp360Field
                applicationId={app?.applicationId}
                editField="status"
                editComponent="appGroup"
                options={statuses}
                editValue={app?.appGroup}
                edit={edit}
                hasEditPermission={canEditInfo()}
              >
                <AppInfoItem
                  primaryText="Application Group:"
                  secondaryText={app?.appGroup}
                />
              </EditableApp360Field>
              <EditableApp360Field
                applicationId={app?.applicationId}
                editField="url"
                editComponent="urlEdit"
                editValue={app?.url}
                edit={edit}
                hasEditPermission={canEditInfo()}
              >
                <AppInfoItem
                  link={app?.url}
                  primaryText="URL:"
                  secondaryText={app?.url ? app?.url : 'Unassigned'}
                />
              </EditableApp360Field>
              {app?.corporateEntity === 'Cable' && (
                <>
                  <EditableApp360Field
                    applicationId={app?.applicationId}
                    editField="serviceLevelTier"
                    editComponent="serviceLevelTier"
                    appInfo={app}
                    editValue={`${app?.serviceLevel} - ${app?.serviceLevelName}`}
                    options={serviceLevelDomains}
                    edit={edit}
                    hasEditPermission={canEditInfo()}
                  >
                    <AppInfoItem
                      primaryText="Service Level Tier:"
                      secondaryText={`${app?.serviceLevel} - ${app?.serviceLevelName}`}
                    />
                  </EditableApp360Field>

                  {app?.systemType !== 'AiSystem' && (
                    <EditableApp360Field
                      applicationId={app?.applicationId}
                      editField="informationClassificationPolicy"
                      editComponent="informationClassification"
                      editValue={app?.informationClassificationPolicy}
                      edit={edit}
                      hasEditPermission={canEditInfo()}
                    >
                      <AppInfoItem
                        primaryText="Information Classification:"
                        secondaryText={
                          app?.informationClassificationPolicy?.name
                            ? app?.informationClassificationPolicy?.name
                            : 'Not Classified'
                        }
                      />
                    </EditableApp360Field>
                  )}
                  <EditableApp360Field
                    applicationId={app?.applicationId}
                    editField="functionalArea"
                    editComponent="functionalArea"
                    options={statuses}
                    editValue={getFunctionalArea().name}
                    edit={edit}
                    hasEditPermission={canEditFunctionalArea()}
                  >
                    <AppInfoItem
                      primaryText="Functional Area"
                      secondaryText={getFunctionalArea().name}
                    />
                  </EditableApp360Field>
                  {ConfigHelper.instance.isFeatureEnabled(
                    Feature.BUSINESS_CAPABILITIES,
                    false,
                  ) && (
                    <EditableBusinessCapabilities
                      applicationId={app?.applicationId}
                      businessCapabilities={app?.iopBusinessCapabilities ?? []}
                      edit={edit}
                      hasEditPermission={canEditInfo()}
                    />
                  )}
                  <EditableServiceOfferings
                    applicationId={app?.applicationId}
                    serviceOfferings={app?.iopServiceOfferings ?? []}
                    edit={edit}
                    hasEditPermission={canEditInfo()}
                  />
                  <EditableServiceBusinessItems
                    applicationId={app?.applicationId}
                    serviceBusinessItems={app?.iopServiceBusinessItems ?? []}
                    edit={edit}
                    hasEditPermission={canEditInfo()}
                  />
                  <AppInfoItem
                    primaryText="SDL Team"
                    secondaryText={sdlTeamName}
                  />
                </>
              )}
            </>
          </DevHubPaper>
        </DevHubGrid>

        <DevHubGrid item xs={12} md={6}>
          <DevHubPaper fullHeight>
            <>
              <AppInfoItem
                primaryText="Application ID:"
                secondaryText={app?.applicationId}
              />
              {app?.corporateEntity === 'Cable' &&
                app?.systemType !== 'AiSystem' && (
                  <>
                    <AppInfoItem
                      primaryText="IOP Owner:"
                      secondaryText={`${app?.iopOwner}`}
                    />
                    <AppInfoItem
                      primaryText="IOP Support Group:"
                      secondaryText={`${app?.iopSupportGroup}`}
                    />
                  </>
                )}
              <AppInfoItem
                primaryText="Updated At:"
                secondaryText={app?.updatedAt?.split('T')[0]}
              />
              <AppInfoItem
                primaryText="Created At:"
                secondaryText={app?.createdAt?.split('T')[0]}
              />
              <AppInfoItem
                primaryText="Created By:"
                secondaryText={`${app?.createdBy?.firstName} ${app?.createdBy?.lastName}`}
              />
            </>
          </DevHubPaper>
        </DevHubGrid>
      </DevHubGrid>
    </>
  );
};
